import { ReactNode, useEffect, useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { cn } from '@amusnet/react-ui'

import { useConfigurationStore, useFreeBuyBonusStore, useFreeSpinsBonusesStore, useJackpotStore } from '@stores'
import { ViewportType } from 'game-ui-types'

import { CloseButtonSVG } from './'

type BonusModalProps = {
    children: ReactNode
    isOpen: boolean
    width?: string | { portrait?: string; landscape?: string }
    showCloseIcon?: boolean
    closeOnOutsideClick?: boolean
    contentShadow?: boolean
    onClose?: () => void
    scale?: number
    bonusId?: string
}

export const BonusModal = ({
    isOpen,
    onClose,
    showCloseIcon = true,
    closeOnOutsideClick = true,
    contentShadow = true,
    width,
    children,
    scale,
    bonusId,
}: BonusModalProps) => {
    const viewport = useConfigurationStore((state) => state.viewport)
    const buyBonusConfig = useConfigurationStore((state) => state.buyBonusConfig)
    const isFBBModalOpened = useFreeBuyBonusStore((state) => state.isModalOpened)
    const isFSModalOpened = useFreeSpinsBonusesStore((state) => state.isModalOpened)

    const portraitViewport = viewport === ViewportType.MOBILE_PORTRAIT

    const hasOpened = useRef(false)
    const targetScale = scale && !portraitViewport ? scale : 1

    const modalWidth =
        typeof width === 'string'
            ? width
            : portraitViewport
              ? (width?.portrait ?? '746px')
              : (width?.landscape ?? '746px')

    const portraitStyles =
        portraitViewport && (isFBBModalOpened || isFSModalOpened)
            ? { alignItems: 'baseline', paddingTop: '275px' }
            : { alignItems: 'center' }
    const modalContentShadowClass = contentShadow ? 'shadow-[0_10px_10px_rgba(0,0,0,0.6)]' : ''
    const closeJackpotHistory = useJackpotStore((state) => state.closeJackpotHistory)
    const modalRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (isOpen) {
            closeJackpotHistory()
        } else {
            hasOpened.current = false
        }
    }, [isOpen])

    useEffect(() => {
        hasOpened.current = false
    }, [bonusId])

    useGSAP(() => {
        if (isOpen && modalRef.current) {
            if (!hasOpened.current) {
                gsap.fromTo(
                    modalRef.current,
                    { scale: 0 },
                    { scale: targetScale, duration: 0.6, ease: 'back.out(1.7)' }
                )
                hasOpened.current = true
            } else {
                gsap.to(modalRef.current, { scale: targetScale, duration: 0.3, ease: 'power2.out' })
            }
        }
    }, [isOpen, targetScale, bonusId])

    if (!isOpen) return null

    return (
        <div id="modal-inset" style={portraitStyles} className="fixed inset-0 z-[203] flex justify-center">
            <div className="absolute inset-0 bg-black opacity-50" onClick={closeOnOutsideClick ? onClose : undefined} />
            <div
                id="bonus-modal"
                ref={modalRef}
                role="dialog"
                aria-modal="true"
                style={{ width: modalWidth }}
                className={cn(
                    'relative z-10 flex flex-col rounded-[10px] text-white overflow-hidden',
                    modalContentShadowClass
                )}
            >
                {showCloseIcon && (
                    <button
                        id="close-modal"
                        onClick={onClose}
                        aria-label="Close modal"
                        className="absolute top-2 right-2 p-1 z-20 inline-flex items-center justify-center"
                    >
                        <span className="absolute inset-1 rounded-full overflow-hidden backdrop-blur-md bg-white/1" />
                        <div className="relative z-10">
                            <CloseButtonSVG width={49} height={49} color={buyBonusConfig.layout?.closeButtonColor} />
                        </div>
                    </button>
                )}
                {children}
            </div>
        </div>
    )
}
